// ============================================================
// FILE: SuburbSearch.js
// LOCATION: C:\gardenmarket\web\src\components\SuburbSearch.js
// ============================================================
// PURPOSE:
//   Reusable suburb search component with country selector
//   and type-ahead filtering. Used on:
//     - Register.js (buyer and gardener home suburb)
//     - GardenerProfile.js (service area search)
//     - PostJob.js (job location)
//
// PROPS:
//   value         — currently selected suburb object or null
//   onChange      — called with selected suburb object
//   country       — currently selected country code (controlled)
//   onCountryChange — called when country changes
//   label         — field label text
//   placeholder   — input placeholder
//   showCountry   — whether to show the country selector (default true)
//   required      — HTML required attribute on the input
// ============================================================

import { useState, useEffect, useRef } from 'react';
import client from '../api/client';
import { colors, radius } from '../theme/theme';

const COUNTRIES = [
    { code: 'AU', name: 'Australia',     flag: '🇦🇺' },
    { code: 'NZ', name: 'New Zealand',   flag: '🇳🇿' },
    { code: 'US', name: 'United States', flag: '🇺🇸' },
];

export default function SuburbSearch({
    value,
    onChange,
    country,
    onCountryChange,
    label        = 'Suburb',
    placeholder  = 'Type suburb name...',
    showCountry  = true,
    required     = false,
}) {
    const [query,    setQuery]    = useState('');
    const [results,  setResults]  = useState([]);
    const [open,     setOpen]     = useState(false);
    const [loading,  setLoading]  = useState(false);
    const debounceRef = useRef(null);
    const wrapRef     = useRef(null);

    // Show selected suburb name in input when value is set
    useEffect(() => {
        if (value) {
            setQuery(`${value.suburb_name} ${value.postcode}`);
            setOpen(false);
            setResults([]);
        }
    }, [value]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleInput = (e) => {
        const q = e.target.value;
        setQuery(q);
        // Clear selection when user starts typing again
        if (value) onChange(null);

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (q.trim().length < 2) {
            setResults([]);
            setOpen(false);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const params = { q: q.trim() };
                if (country) params.country = country;
                const r = await client.get('/ref/suburbs/search', { params });
                setResults(r.data.suburbs || []);
                setOpen(true);
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 250);
    };

    const handleSelect = (suburb) => {
        onChange(suburb);
        setQuery(`${suburb.suburb_name} ${suburb.postcode}`);
        setOpen(false);
        setResults([]);
    };

    const handleCountryChange = (e) => {
        onCountryChange?.(e.target.value);
        onChange(null);
        setQuery('');
        setResults([]);
    };

    const handleClear = () => {
        onChange(null);
        setQuery('');
        setResults([]);
    };

    return (
        <div>
            {label && <label style={styles.label}>{label}{required && ' *'}</label>}
            <div style={styles.row}>
                {showCountry && (
                    <select
                        value={country || 'AU'}
                        onChange={handleCountryChange}
                        style={styles.countrySelect}
                    >
                        {COUNTRIES.map(c => (
                            <option key={c.code} value={c.code}>
                                {c.flag} {c.name}
                            </option>
                        ))}
                    </select>
                )}
                <div ref={wrapRef} style={styles.searchWrap}>
                    <input
                        type="text"
                        value={query}
                        onChange={handleInput}
                        placeholder={placeholder}
                        style={styles.input}
                        autoComplete="off"
                    />
                    {loading && <span style={styles.spinner}>⏳</span>}
                    {value && (
                        <button type="button" onClick={handleClear} style={styles.clearBtn} title="Clear">×</button>
                    )}
                    {open && results.length > 0 && (
                        <div style={styles.dropdown}>
                            {results.map(s => (
                                <div
                                    key={s.suburb_id}
                                    style={styles.option}
                                    onMouseDown={() => handleSelect(s)}
                                >
                                    <span style={styles.optionName}>{s.suburb_name}</span>
                                    <span style={styles.optionMeta}>
                                        {s.postcode}
                                        {s.state ? `, ${s.state}` : ''}
                                        {showCountry && s.country !== (country || 'AU') ? ` · ${s.country}` : ''}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                    {open && results.length === 0 && query.length >= 2 && !loading && (
                        <div style={styles.dropdown}>
                            <div style={styles.noResults}>No suburbs found for "{query}"</div>
                        </div>
                    )}
                </div>
            </div>
            {value && (
                <div style={styles.selected}>
                    ✓ {value.suburb_name} {value.postcode}
                    {value.state ? `, ${value.state}` : ''}
                </div>
            )}
        </div>
    );
}

const styles = {
    label:        { display:'block', fontWeight:600, marginBottom:'6px', color:'#333', fontSize:'0.9rem' },
    row:          { display:'flex', gap:'8px', alignItems:'flex-start' },
    countrySelect:{ padding:'10px 8px', border:`1px solid ${colors.border}`, borderRadius:radius.sm, fontSize:'0.9rem', backgroundColor:'white', cursor:'pointer', minWidth:'160px' },
    searchWrap:   { position:'relative', flex:1 },
    input:        { width:'100%', padding:'10px 36px 10px 12px', border:`1px solid ${colors.border}`, borderRadius:radius.sm, fontSize:'0.95rem', boxSizing:'border-box' },
    spinner:      { position:'absolute', right:'32px', top:'50%', transform:'translateY(-50%)', fontSize:'0.85rem' },
    clearBtn:     { position:'absolute', right:'8px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', fontSize:'1.2rem', color:colors.textFaint, cursor:'pointer', lineHeight:1, padding:'0 4px' },
    dropdown:     { position:'absolute', top:'100%', left:0, right:0, backgroundColor:'white', border:`1px solid ${colors.border}`, borderRadius:radius.sm, boxShadow:'0 4px 12px rgba(0,0,0,0.12)', zIndex:1000, maxHeight:'240px', overflowY:'auto', marginTop:'2px' },
    option:       { padding:'10px 14px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:`1px solid ${colors.border}` },
    optionName:   { fontWeight:600, color:colors.text, fontSize:'0.9rem' },
    optionMeta:   { fontSize:'0.8rem', color:colors.textMuted },
    noResults:    { padding:'12px 14px', color:colors.textMuted, fontSize:'0.88rem', textAlign:'center' },
    selected:     { marginTop:'6px', fontSize:'0.82rem', color:colors.brand, fontWeight:500 },
};
